import { createJourney } from '@platform/journey';
import { z } from 'zod';

/**
 * The booking journey instance: which product to book plus voucher/campaign,
 * captured at entry and resumed across register -> completion -> payment -> confirm.
 *
 * Lives in the Hub for now. It moves to lib/booking (WWP-467) once that exists,
 * since the intent shape and its param mapping are booking-domain, not generic
 * journey machinery. `@platform/journey` stays product-agnostic.
 */

/** `voucher`/`campaignCode` are the entry's `vc`/`cc`. */
export const bookingIntentSchema = z.object({
  product: z.string().min(1),
  voucher: z.string().optional(),
  campaignCode: z.string().optional(),
});

export type BookingIntent = z.infer<typeof bookingIntentSchema>;

/**
 * No product means this is not a booking entry, so capture is a no-op and any
 * existing intent survives a stray re-entry (auth redirect, remount). The lead
 * product for a bare journey entry is the landing route's call: it fills `product`
 * before capturing. This mapper never invents one.
 */
export function parseBookingIntent(params: Record<string, string>): BookingIntent | null {
  if (!params.product) {
    return null;
  }
  return {
    product: params.product,
    ...(params.vc && { voucher: params.vc }),
    ...(params.cc && { campaignCode: params.cc }),
  };
}

/**
 * The instance's `JourneyProvider` is re-exported as a component-only module from
 * ./BookingJourneyProvider, so this wiring module, which also exports the hook and
 * schema, stays a clean Fast-Refresh boundary (ADR-019).
 */
export const bookingJourney = createJourney({
  type: 'booking',
  schema: bookingIntentSchema,
  parse: parseBookingIntent,
});

/** Read the booking journey inside components. */
export const useBookingJourney = bookingJourney.useJourney;

/**
 * Route loader for a booking entry point. The raw query string, not the route's validated search:
 * a schema would have to restate every `sc_*`/`utm_*` key, and TanStack's parsed form coerces
 * values, which drops a numeric voucher.
 */
export function captureBookingEntry({ location }: { location: { searchStr: string } }): void {
  bookingJourney.client.capture(new URLSearchParams(location.searchStr));
}
